import classnames from 'classnames'
import { type FormikHelpers } from 'formik'
import * as React from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { EmailIcon } from 'src/assets/icons/customIcons/Email'
import { EnvelopeIcon } from 'src/assets/icons/customIcons/Envelope'
import { errorMessage } from 'src/helpers/fns'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { type AuthenticatedHeaders, useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import { Button } from 'src/tailwind/components/Button'
import { Modal } from 'src/tailwind/components/Modal'
import * as Table from 'src/tailwind/components/Table'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import TextareaInput from 'src/views/components/forms/formik/TextareaInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import Loader from 'src/views/components/Loader'
import Select from 'src/views/components/Select'
import { FormSection } from '../../programs/AdministratorProgramPage/FormSection'

const WITHOUT_GROUP_ID = '__without_group__'
/** `groupId=0` is how the API asks for the choices that belong to no group. */
const WITHOUT_GROUP_QUERY_ID = 0
/** The course-wide totals come from `meta.summary`, so the rows themselves are dead weight. */
const SUMMARY_ONLY_PER_PAGE = 1
/**
 * Old Argus lists a group in full, so take the whole group in one page. 1000 is the API's
 * ceiling: anything larger is ignored and silently falls back to 20 per page.
 */
const GROUP_STUDENTS_PER_PAGE = 1000

interface SelectOption {
  readonly id: string
  readonly name: string
}

interface MessageRecipient {
  readonly id: number
  readonly fullName: string
}

export default function AdministratorCourseStudentsPage(): JSX.Element {
  return <PageContent />
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const { isMainAdministrator, canManage } = useAdminAccess()
  const canManageCourses = canManage('courses')
  const { courseId } = useParams()
  const [searchParams] = useSearchParams()
  const activeSemester = useActiveSemester()
  const semIdStr = searchParams.get('semId') ?? (activeSemester?.id != null ? String(activeSemester.id) : '')
  const parsedSemId = semIdStr !== '' ? parseInt(semIdStr, 10) : null

  const [isReplaceFormOpen, setIsReplaceFormOpen] = React.useState(false)
  const [expandedGroupId, setExpandedGroupId] = React.useState<string | null>(WITHOUT_GROUP_ID)

  const { data: groups, mutate: mutateGroups } = useApi({
    endpoint: Api.getAdministratorCourseGroups,
    params: React.useMemo(
      () => ({ headers, args: { id: courseId! }, query: parsedSemId != null ? { semId: parsedSemId } : {} }),
      [courseId, headers, parsedSemId]
    ),
    shouldFetch: courseId != null,
  })

  // Only `meta.summary` is read here: it holds the course-wide counts. The per-group lists are
  // fetched by `<GroupDetailPanel>`.
  const {
    data: summaryResponse,
    isLoading: isSummaryLoading,
    mutate: mutateSummary,
  } = useApi({
    endpoint: Api.getAdministratorCourseStudents,
    params: React.useMemo(
      () => ({
        headers,
        args: { id: courseId! },
        query: { semId: parsedSemId ?? 0, perPage: SUMMARY_ONLY_PER_PAGE },
      }),
      [courseId, headers, parsedSemId]
    ),
    shouldFetch: courseId != null && parsedSemId != null,
    suspense: false,
  })
  const summary = summaryResponse?.meta.summary

  const selectedGroup = React.useMemo(
    () => (groups ?? []).find((group) => group.id === expandedGroupId),
    [groups, expandedGroupId]
  )

  const toggleReplaceForm = React.useCallback(() => setIsReplaceFormOpen((prev) => !prev), [])
  const collapseGroup = React.useCallback(() => setExpandedGroupId(null), [])
  const toggleGroup = React.useCallback(
    (groupId: string) => setExpandedGroupId((prev) => (prev === groupId ? null : groupId)),
    []
  )

  // Moving a student between groups changes every count on the page, not just the panel's own.
  const refetchCounts = React.useCallback(async () => {
    await Promise.all([mutateSummary(), mutateGroups()])
  }, [mutateSummary, mutateGroups])

  const handleReplaceNamesSuccess = React.useCallback(() => {
    setIsReplaceFormOpen(false)
    void refetchCounts()
  }, [refetchCounts])

  const handleAssignmentsSaved = React.useCallback(() => {
    void refetchCounts()
    setExpandedGroupId(null)
  }, [refetchCounts])

  if (courseId == null) return <Loader className="m-auto mt-8 flex" />

  return (
    <div className="mt-4 w-full space-y-6">
      <fieldset className="min-w-0 rounded-card bg-card px-6 py-8">
        {canManageCourses && (
          <div className="mb-4 flex justify-end gap-3 border-b border-borderGrey pb-2">
            <button
              type="button"
              className="shrink-0 text-sm text-primaryBlueLink hover:underline"
              onClick={toggleReplaceForm}
            >
              {t('course:replace_names')}
            </button>
          </div>
        )}

        {canManageCourses && isReplaceFormOpen && (
          <FormSection title={t('course:replace_names')} className="mb-4">
            <ReplaceNamesForm
              headers={headers}
              courseId={courseId}
              semId={parsedSemId}
              onSuccess={handleReplaceNamesSuccess}
            />
          </FormSection>
        )}

        {isSummaryLoading ? (
          <Loader className="m-auto flex py-8" />
        ) : (
          <>
            <p className="mb-4 text-sm text-primaryTextColor">
              {t(['სულ კურსი', 'Total course'])}: {summary?.total ?? 0} / {t(['ჩაჭრილი', 'Failed'])}:{' '}
              {summary?.failed ?? 0}
            </p>
            <Table.Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t(['კურსის ჯგუფები', 'Course groups'])}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(groups ?? []).map((group) => (
                  <CourseGroupTableRow
                    key={group.id}
                    groupId={group.id}
                    label={group.name}
                    min={group.quotaMin}
                    max={group.quotaMax}
                    studentCount={group.studentCount ?? 0}
                    lecturers={group.lecturers ?? []}
                    isSelected={expandedGroupId === group.id}
                    onSelect={toggleGroup}
                  />
                ))}
                <CourseGroupTableRow
                  key="without-groups"
                  groupId={WITHOUT_GROUP_ID}
                  label={t(['ჯგუფის გარეშე', 'Without groups'])}
                  studentCount={summary?.withoutGroup ?? 0}
                  lecturers={[]}
                  isSelected={expandedGroupId === WITHOUT_GROUP_ID}
                  onSelect={toggleGroup}
                />
              </Table.Tbody>
            </Table.Table>

            {expandedGroupId != null && (
              <GroupDetailPanel
                key={expandedGroupId}
                headers={headers}
                courseId={courseId}
                semId={parsedSemId}
                groupQueryId={
                  expandedGroupId === WITHOUT_GROUP_ID ? WITHOUT_GROUP_QUERY_ID : parseInt(expandedGroupId, 10)
                }
                title={
                  expandedGroupId === WITHOUT_GROUP_ID
                    ? `${t(['ჯგუფი', 'Group'])}: ${t(['ჯგუფის გარეშე', 'Without groups'])} - ( - )`
                    : `${t(['ჯგუფი', 'Group'])}: ${selectedGroup?.name ?? ''} - (${selectedGroup?.quotaMin ?? 0} - ${selectedGroup?.quotaMax ?? 0})`
                }
                isMainAdministrator={isMainAdministrator}
                lecturers={selectedGroup?.lecturers ?? []}
                totalCount={
                  expandedGroupId === WITHOUT_GROUP_ID
                    ? (summary?.withoutGroup ?? 0)
                    : (selectedGroup?.studentCount ?? 0)
                }
                failedCount={expandedGroupId === WITHOUT_GROUP_ID ? null : (selectedGroup?.failedCount ?? 0)}
                groups={groups ?? []}
                onClose={collapseGroup}
                onSaved={handleAssignmentsSaved}
              />
            )}
          </>
        )}
      </fieldset>
    </div>
  )
}

function CourseGroupTableRow({
  groupId,
  label,
  min,
  max,
  studentCount,
  lecturers,
  isSelected,
  onSelect,
}: {
  readonly groupId: string
  readonly label: string
  readonly min?: number
  readonly max?: number
  readonly studentCount: number
  readonly lecturers: readonly Api.AdministratorCourseGroupLecturer[]
  readonly isSelected: boolean
  readonly onSelect: (groupId: string) => void
}): JSX.Element {
  const t = useTranslatable()
  const lecturerNames = lecturers.map((lecturer) => lecturer.fullName).join(', ')

  const handleSelect = React.useCallback(() => onSelect(groupId), [onSelect, groupId])

  return (
    <Table.Tr className={classnames({ 'bg-lightBlue': isSelected })}>
      <Table.Td>
        <button type="button" className="font-medium text-primaryBlueLink hover:underline" onClick={handleSelect}>
          {label}
        </button>
        {min != null && max != null && (
          <>
            {' '}
            - ({min} - {max})
          </>
        )}{' '}
        - ({t(['სულ', 'Total'])}: {studentCount}){lecturerNames.length > 0 ? ` - ${lecturerNames}` : ''}
      </Table.Td>
    </Table.Tr>
  )
}

interface ReplaceNamesFormValues {
  readonly from: string
  readonly to: string
}

function ReplaceNamesForm({
  headers,
  courseId,
  semId,
  onSuccess,
}: {
  readonly headers: AuthenticatedHeaders
  readonly courseId: string
  readonly semId: number | null
  readonly onSuccess: () => void
}): JSX.Element {
  const t = useTranslatable()

  const onSubmit = React.useCallback(
    async (values: ReplaceNamesFormValues, { resetForm }: FormikHelpers<ReplaceNamesFormValues>) => {
      if (semId == null) return
      const result = await Api.postAdministratorCourseStudentsReplaceNames({
        headers,
        args: { id: courseId },
        // The API matches the chosen name exactly, so surrounding spaces — pasting the name in
        // picks them up easily — would match nothing at all.
        body: { semId, from: values.from.trim(), to: values.to.trim() },
      })
      // The page never shows the chosen names, so a replacement that matched nothing looks exactly
      // like one that worked. Keep the form open with the mismatch spelled out instead.
      if (result.data.updated === 0) {
        throw { type: 'ErrorMessage' as const, message: t('course:replace_names_not_found') }
      }
      toast.success(`${t('common:updated_successfully')} (${result.data.updated})`)
      resetForm()
      onSuccess()
    },
    [headers, courseId, semId, onSuccess, t]
  )

  return (
    <Form<ReplaceNamesFormValues> initialValues={{ from: '', to: '' }} onSubmit={onSubmit}>
      <FormError />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextInput required type="text" name="from" label={t('course:replace_name_from')} />
        <TextInput required type="text" name="to" label={t('course:replace_name_to')} />
      </div>
      <div className="mt-4 flex justify-end">
        <Button type="submit" variant="blue" disabled={semId == null}>
          {t('course:replace')}
        </Button>
      </div>
    </Form>
  )
}

function GroupDetailPanel({
  headers,
  courseId,
  semId,
  groupQueryId,
  title,
  isMainAdministrator,
  lecturers,
  totalCount,
  failedCount,
  groups,
  onClose,
  onSaved,
}: {
  readonly headers: AuthenticatedHeaders
  readonly courseId: string
  readonly semId: number | null
  readonly groupQueryId: number
  readonly title: string
  readonly isMainAdministrator: boolean
  readonly lecturers: readonly Api.AdministratorCourseGroupLecturer[]
  readonly totalCount: number
  /** Null for the without-group panel, which the summary has no failed count for. */
  readonly failedCount: number | null
  readonly groups: readonly Api.AdministratorCourseGroupItem[]
  readonly onClose: () => void
  readonly onSaved: () => void
}): JSX.Element {
  const t = useTranslatable()
  const canManageCourses = useAdminAccess().canManage('courses')
  const [groupOverrides, setGroupOverrides] = React.useState<Readonly<Record<number, string>>>({})
  const [messageRecipients, setMessageRecipients] = React.useState<readonly MessageRecipient[] | null>(null)
  const [isSaving, setIsSaving] = React.useState(false)

  const { data: studentsResponse, isLoading } = useApi({
    endpoint: Api.getAdministratorCourseStudents,
    params: React.useMemo(
      () => ({
        headers,
        args: { id: courseId },
        query: { semId: semId ?? 0, groupId: groupQueryId, perPage: GROUP_STUDENTS_PER_PAGE },
      }),
      [headers, courseId, semId, groupQueryId]
    ),
    shouldFetch: semId != null,
    suspense: false,
  })
  const students = React.useMemo(() => studentsResponse?.data ?? [], [studentsResponse])

  const resolveGroupId = React.useCallback(
    (student: Api.AdministratorCourseStudentChoice) =>
      groupOverrides[student.userId] ?? (student.groupId != null ? String(student.groupId) : ''),
    [groupOverrides]
  )

  const groupOptions: readonly SelectOption[] = React.useMemo(
    () => [
      { id: '', name: t(['ჯგუფის გარეშე', 'Without group']) },
      ...groups.map((group) => ({ id: group.id, name: group.name })),
    ],
    [groups, t]
  )

  const handleGroupChange = React.useCallback((userId: number, groupId: string) => {
    setGroupOverrides((prev) => ({ ...prev, [userId]: groupId }))
  }, [])

  const handleMessageStudent = React.useCallback((recipient: MessageRecipient) => {
    setMessageRecipients([recipient])
  }, [])

  const handleMessageGroup = React.useCallback(() => {
    setMessageRecipients(students.map((student) => ({ id: student.userId, fullName: student.user.fullName })))
  }, [students])

  const handleCloseMessageModal = React.useCallback(() => setMessageRecipients(null), [])

  const handleSave = React.useCallback(async () => {
    if (semId == null) return
    setIsSaving(true)
    try {
      await Api.putAdministratorCourseStudents({
        headers,
        args: { id: courseId },
        body: {
          semId,
          assignments: students.map((student) => {
            const groupId = resolveGroupId(student)
            return { userId: student.userId, groupId: groupId === '' ? null : parseInt(groupId, 10) }
          }),
        },
      })
      toast.success(t('common:updated_successfully'))
      onSaved()
    } catch (error) {
      toast.error(errorMessage(error as base.CommonError))
    } finally {
      setIsSaving(false)
    }
  }, [semId, headers, courseId, students, resolveGroupId, t, onSaved])

  const onSaveClick = React.useCallback(() => void handleSave(), [handleSave])

  return (
    <div className="mt-4 overflow-hidden rounded-card border border-borderGrey">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-borderGrey bg-card px-4 py-3">
        <span className="font-medium text-primaryTextColor">{title}</span>
        {lecturers.length > 0 && (
          <span className="text-sm text-primaryTextColor">
            {lecturers.map((lecturer) => lecturer.fullName).join(', ')}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <span className="text-sm text-primaryTextColor">
          {t(['სულ', 'Total'])}: {totalCount} / {t(['ჩაჭრილი', 'Failed'])}:{' '}
          {failedCount ?? students.filter((student) => student.status !== 'PASSED').length}
        </span>
        <button
          type="button"
          className="text-sm text-primaryBlueLink hover:underline disabled:opacity-50"
          disabled={students.length === 0}
          onClick={handleMessageGroup}
        >
          {t(['ჯგუფის ყველა წევრისთვის შეტყობინების გაგზავნა', 'Message to every student of the group'])}
        </button>
      </div>

      {isLoading ? (
        <Loader className="m-auto flex py-8" />
      ) : (
        <div className="divide-y divide-borderGrey border-t border-borderGrey">
          {students.map((student) => (
            <GroupStudentRow
              key={student.uid}
              student={student}
              isMainAdministrator={isMainAdministrator}
              groupOptions={groupOptions}
              selectedGroupId={resolveGroupId(student)}
              onGroupChange={handleGroupChange}
              onMessage={handleMessageStudent}
            />
          ))}
        </div>
      )}

      <div className="flex justify-end gap-2 border-t border-borderGrey px-4 py-3">
        <Button variant="outline" type="button" onClick={onClose}>
          {t('common:cancel')}
        </Button>
        {canManageCourses && (
          <Button variant="blue" type="button" disabled={semId == null || isSaving} onClick={onSaveClick}>
            {t('common:save')}
          </Button>
        )}
      </div>

      {messageRecipients != null && (
        <CourseMessageModal headers={headers} recipients={messageRecipients} onClose={handleCloseMessageModal} />
      )}
    </div>
  )
}

function GroupStudentRow({
  student,
  isMainAdministrator,
  groupOptions,
  selectedGroupId,
  onGroupChange,
  onMessage,
}: {
  readonly student: Api.AdministratorCourseStudentChoice
  readonly isMainAdministrator: boolean
  readonly groupOptions: readonly SelectOption[]
  readonly selectedGroupId: string
  readonly onGroupChange: (userId: number, groupId: string) => void
  readonly onMessage: (recipient: MessageRecipient) => void
}): JSX.Element {
  const t = useTranslatable()
  const canManageCourses = useAdminAccess().canManage('courses')

  const handleGroupChange = React.useCallback(
    (option: SelectOption | null) => onGroupChange(student.userId, option?.id ?? ''),
    [onGroupChange, student.userId]
  )

  const handleMessage = React.useCallback(
    () => onMessage({ id: student.userId, fullName: student.user.fullName }),
    [onMessage, student.userId, student.user.fullName]
  )

  // Old Argus prints the chooser's own name, whoever they are, and only marks the student's own
  // choice. The name is PII, so everyone but the main administrator sees a static label instead.
  const chooser =
    student.chooserUserId === student.userId
      ? t('choice:chosen_by_self')
      : isMainAdministrator && student.chooserFullName != null && student.chooserFullName !== ''
        ? student.chooserFullName
        : t('choice:chosen_by_administrator')

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 text-sm">
      <div className="flex items-center gap-2">
        <span className="text-primaryTextColor">
          {student.user.fullName} ({student.user.persNum})
        </span>
        <button
          type="button"
          className="flex size-6 items-center justify-center rounded-full hover:bg-hover-icon"
          aria-label={t('message:send_message')}
          onClick={handleMessage}
        >
          <span className="block size-4">
            <EnvelopeIcon />
          </span>
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <div className="w-80">
          <Select<SelectOption>
            options={groupOptions}
            value={groupOptions.find((option) => option.id === selectedGroupId) ?? groupOptions[0]}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.name}
            onChange={handleGroupChange}
            menuPortalTarget={document.body}
            isDisabled={!canManageCourses}
          />
        </div>
        <span className="w-44 whitespace-nowrap text-captionColor">{student.createdAt}</span>
        <span className="w-60 whitespace-nowrap text-captionColor">{chooser}</span>
        <span className="w-12 text-right text-primaryTextColor">{student.score ?? '-'}</span>
      </div>
    </div>
  )
}

interface MessageFormValues {
  readonly subject: string
  readonly body: string
}

function CourseMessageModal({
  headers,
  recipients,
  onClose,
}: {
  readonly headers: AuthenticatedHeaders
  readonly recipients: readonly MessageRecipient[]
  readonly onClose: () => void
}): JSX.Element {
  const t = useTranslatable()

  const onSubmit = React.useCallback(
    async (values: MessageFormValues) => {
      await Api.postMessages({
        headers,
        body: {
          recipients: recipients.map((recipient) => String(recipient.id)),
          subject: values.subject,
          body: values.body,
        },
      })
      toast.success(t('uncategorized:message_successfully_sent'))
      onClose()
    },
    [headers, recipients, onClose, t]
  )

  return (
    <Modal title={t('message:messages_compose')} icon={<EmailIcon />} isModalOpen onClose={onClose}>
      <Form<MessageFormValues>
        initialValues={{ subject: '', body: '' }}
        onSubmit={onSubmit}
        classNames={{ form: 'overflow-auto py-4' }}
      >
        <FormError />
        <div className="rounded-[12px] border border-borderGrey">
          <div className="flex flex-wrap items-center border-b border-borderGrey px-[24px] py-[11px]">
            <label className="mr-[12px] font-bold text-primaryTextColor">{t('message:recipients')}:</label>
            <span className="text-primaryTextColor">
              {recipients.map((recipient) => recipient.fullName).join(', ')}
            </span>
          </div>
          <div className="border-b border-borderGrey px-[24px] py-[11px]">
            <TextInput
              isHorizontal
              type="text"
              name="subject"
              className="!mb-0"
              placeholder={t('message:enter_the_title')}
              label={t('message:subject')}
              required
            />
          </div>
          <div className="mb-3 flex w-full px-[24px] py-[11px]">
            <TextareaInput
              label={t('message:message_text')}
              name="body"
              placeholder={t('message:enter_the_message_text')}
              rows={7}
              required
            />
          </div>
          <div className="mb-[16px] mt-4 flex w-full justify-end border-t border-borderGrey pr-[24px] pt-[11px]">
            <Button variant="red" type="submit">
              {t('message:send')}
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  )
}
